/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================
*/

import { useEffect, useState } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavCard from "examples/Sidenav/SidenavCard";

import SidenavRoot from "examples/Sidenav/SidenavRoot";
import { useSoftUIController, setMiniSidenav } from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;

  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return { name: "", email: "" };
      const u = JSON.parse(raw);
      return { name: u.fullName || "", email: u.email || "" };
    } catch {
      return { name: "", email: "" };
    }
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "user") {
        try {
          const u = e.newValue ? JSON.parse(e.newValue) : {};
          setUser({ name: u.fullName || "", email: u.email || "" });
        } catch {
          setUser({ name: "", email: "" });
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    function handleMiniSidenav() {
      if (window.innerWidth >= 1200) setMiniSidenav(dispatch, false);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // ✅ Only one submenu open at a time
  const [openCollapse, setOpenCollapse] = useState(null);

  // ✅ Route change pe submenu auto close
  useEffect(() => {
    setOpenCollapse(null);
  }, [pathname]);

  const childWrapSx = (isOpen) => ({
    ml: 1.5,
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "translateX(0px)" : "translateX(-8px)",
    transition: "all 240ms ease",
  });

  // ✅ Profile ko bottom pe bhejne ke liye split
  const bottomKeys = new Set(["profile"]);
  const topRoutes = routes.filter((r) => !bottomKeys.has(r.key));
  const bottomRoutes = routes.filter((r) => bottomKeys.has(r.key));

  const renderRoutesFrom = (arr) =>
    arr.map((r) => {
      const { type, name, icon, title, noCollapse, key, route, href, collapse } = r;
      let returnValue;

      if (type === "collapse") {
        const hasChildren = Array.isArray(collapse) && collapse.length > 0;

        const isActive =
          (route && (pathname === route || pathname.startsWith(`${route}/`))) ||
          key === collapseName;

        const isChildActive =
          hasChildren &&
          collapse.some(
            (c) => c.route && (pathname === c.route || pathname.startsWith(`${c.route}/`))
          );

        const isOpen = hasChildren && (openCollapse === key || isActive || isChildActive);

        if (hasChildren) {
          returnValue = (
            <SidenavCollapse
              key={key}
              color={color}
              name={name}
              icon={icon}
              active={isActive || isChildActive}
              noCollapse={false}
              open={isOpen}
              onClick={() => {
                setOpenCollapse((prev) => (prev === key ? null : key));
                if (route && pathname !== route) navigate(route);
              }}
            >
              {/* ✅ IMPORTANT: gap equal (no extra gap here) */}
              <List
                disablePadding
                sx={{
                  mt: 0.8,
                  mb: 0.8,
                  pl: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0, // ✅ removed extra gap (submenu = main menu spacing)
                }}
              >
                {collapse.map((c) => {
                  if (c.type !== "collapse") return null;

                  const childActive =
                    c.route && (pathname === c.route || pathname.startsWith(`${c.route}/`));

                  return (
                    <SoftBox key={c.key} sx={childWrapSx(isOpen)}>
                      <NavLink to={c.route} style={{ textDecoration: "none" }}>
                        <SidenavCollapse
                          color={color}
                          name={c.name}
                          icon={c.icon}
                          active={childActive}
                          noCollapse
                        />
                      </NavLink>
                    </SoftBox>
                  );
                })}
              </List>
            </SidenavCollapse>
          );
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                color={color}
                name={name}
                icon={icon}
                active={isActive}
                noCollapse={noCollapse}
              />
            </Link>
          ) : (
            <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
              <SidenavCollapse
                color={color}
                name={name}
                icon={icon}
                active={isActive}
                noCollapse={noCollapse}
              />
            </NavLink>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={3}
            mb={1.4}
            ml={1}
          >
            {title}
          </SoftTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} sx={{ my: 1.6, opacity: 0.35 }} />;
      }

      return returnValue;
    });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>

        {/* ✅ Logo click -> external dashboard */}
        <SoftBox
          component="a"
          href="/dashboard"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: "100%",
            height: "60px",
            padding: 0,
            margin: "-12px 0px",
            textDecoration: "none",
          }}
        >
          {brand && (
            <SoftBox
              component="img"
              src={brand}
              alt="BMAX Logo"
              width="100%"
              height="100%"
              sx={{ objectFit: "contain", padding: 0, margin: 0 }}
            />
          )}
        </SoftBox>
      </SoftBox>

      <Divider />

      {/* ✅ TOP MENUS */}
      <List sx={{ pt: 1.35 }}>{renderRoutesFrom(topRoutes)}</List>

      {/* ✅ Card stays in middle area */}
      <SoftBox pt={2} my={2} mx={2}>
        <SidenavCard />
      </SoftBox>

      {/* ✅ Spacer so Profile goes more bottom */}
      <SoftBox sx={{ flexGrow: 1 }} />

      {/* ✅ BOTTOM MENUS (Profile) */}
      <List sx={{ pb: 1 }}>{renderRoutesFrom(bottomRoutes)}</List>

      {/* ✅ User Name & Email */}
      <SoftBox textAlign="center" pb={2}>
        <SoftTypography variant="caption" fontWeight="bold" color="white" display="block">
          {user.name || "Bruker"}
        </SoftTypography>
        <SoftTypography variant="caption" color="white" display="block">
          {user.email || "Ingen e-post"}
        </SoftTypography>
        <SoftBox
          component="img"
          src="/betongsentrum.png"
          alt="Betongsentrum Logo"
          width="70%"
          height="40px"
        />
      </SoftBox>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = { color: "info", brand: "" };

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
